import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Scrollbars from "rc-scrollbars";
import { IoIosMore } from "react-icons/io";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Avatar, Button, message, Spin } from "antd";
import { PushpinOutlined, PushpinFilled } from "@ant-design/icons";
import { ThemeContext } from "../../../contexts/themeConfig";
import {
  addPinnedAssistant,
  deletePinnedAssistant,
} from "../../../api/pinnedAssistant";
import { getUserID, getUserRole } from "../../../Utility/service";
import "./AssistantList.css";
import { BsRobot } from "react-icons/bs";
import agentPlaceholder from "../../../assests/images/agents-placeholder.png";
import { AssistantContext } from "../../../contexts/AssistantContext";

const AssistantList = ({ propsData }) => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const { searchQuery } = useContext(AssistantContext);
  const {
    assistants,
    setAssistants,
    actions,
    page,
    totalPage,
    loading,
    setAssistantSelected,
    setAssistantIdLinked,
    handleFetchAssistants,
  } = propsData;
  const { assistant_id: ast_id_from_params } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredUser, setHoveredUser] = useState(null);
  const [showPageOneExtra, setShowPageOneExtra] = useState(false);
  const [seeAllMode, setSeeAllMode] = useState(false);

  const role = getUserRole()

  const filteredAssistantListWithTrue = useMemo(
    () => assistants.filter((assistant) => assistant?.is_pinned === true),
    [assistants]
  );

  const filteredAssistantListWithFalse = useMemo(
    () => assistants.filter((assistant) => assistant?.is_pinned === false),
    [assistants]
  );

  const fullAssistantRearrangedList = useMemo(
    () => [...filteredAssistantListWithTrue, ...filteredAssistantListWithFalse],
    [filteredAssistantListWithTrue, filteredAssistantListWithFalse]
  );

  const retrievingUniqueAssistant = useMemo(
    () =>
      fullAssistantRearrangedList.filter(
        (assistant, index, self) =>
          index ===
          self.findIndex(
            (anyAssistant) => anyAssistant?.name === assistant?.name
          )
      ),

    [fullAssistantRearrangedList]
  );

  const sortedUniqueAssistants = useMemo(() => {
    const unique = assistants.filter(
      (assistant, index, self) =>
        index === self.findIndex((a) => a?.name === assistant?.name)
    );
    return unique.sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      const aCount = a.usageCount || 0;
      const bCount = b.usageCount || 0;
      return bCount - aCount;
    });
  }, [assistants]);

  const displayAssistants = useMemo(() => {
    if (!seeAllMode && page === 1) {
      return sortedUniqueAssistants.slice(0, showPageOneExtra ? 8 : 6);
    }
    return sortedUniqueAssistants;
  }, [sortedUniqueAssistants, page, showPageOneExtra, seeAllMode]);

  const handlePinButtonClick = async (
    assistantId,
    id,
    currentPinStatus,
    event
  ) => {
    event.preventDefault();
    // Enforce maximum 3 pinned agents.
    if (!currentPinStatus) {
      const pinnedCount = assistants.filter(
        (assistant) => assistant?.is_pinned
      ).length;
      if (pinnedCount >= 3) {
        message.warning("Maximum 3 agents can be pinned");
        return;
      }
    }
    setIsLoading(true);
    const updatePinStatus = !currentPinStatus;
    try {
      if (updatePinStatus) {
        await addPinnedAssistant(assistantId, id, getUserID(), updatePinStatus);
      } else {
        await deletePinnedAssistant(
          assistantId,
          id,
          getUserID(),
          updatePinStatus
        );
      }
      const updatedAssistants = assistants.map((assistant) =>
        assistant?.assistant_id === assistantId
          ? { ...assistant, is_pinned: updatePinStatus }
          : assistant
      );
      setAssistants(updatedAssistants);
      if (updatePinStatus) {
        const newPinnedCount = updatedAssistants.filter(
          (assistant) => assistant?.is_pinned
        ).length;
        if (newPinnedCount === 3) {
          message.info("3 agents are now pinned");
        }
      }
    } catch (error) {
      console.error("Failed to update pin status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Infinite scroll handler
  const handleScrollFrame = useCallback(
    (values) => {
      const { scrollTop, scrollHeight, clientHeight } = values;
      if (
        scrollTop + clientHeight >= scrollHeight - 10 &&
        !loading &&
        page < totalPage
      ) {
        actions.setPage(page + 1);
      }
    },
    [actions, loading, page, totalPage]
  );

  // Render the list of agents.
  const listContent = (
    <>
      {displayAssistants.map((assistant, index) => (
        <Link
          to={`/agents/${assistant?.assistant_id}`}
          onClick={() => {
            setAssistantIdLinked(assistant?.assistant_id);
            setAssistantSelected(true);
          }}
          rel="noreferrer"
          key={index}
          style={{ textDecoration: "none" }}
        >
          <div
            className="navPrompt assistant-list sidebar-item"
            onMouseEnter={() =>
              !assistant?.is_pinned && setHoveredUser(assistant)
            }
            onMouseLeave={() => !assistant?.is_pinned && setHoveredUser(null)}
          >
            {assistant?.image_url ? (
              <Avatar
                className="agent-image"
                size={20}
                src={assistant?.image_url}
              />
            ) : (
              <img
                className="sidebar-icon"
                width={20}
                height={20}
                src={agentPlaceholder}
                alt=""
              />
            )}
            <p className="sidebar-text agents-name">{assistant?.name}</p>
            {assistant?.is_pinned ? (
              <PushpinFilled
                onClick={(e) =>
                  handlePinButtonClick(
                    assistant?.assistant_id,
                    assistant?._id,
                    assistant?.is_pinned,
                    e
                  )
                }
                style={{
                  marginLeft: "5px",
                  color: "#1890ff",
                  cursor: "pointer",
                }}
                disabled={loading || isLoading}
              />
            ) : (
              hoveredUser?.assistant_id === assistant.assistant_id && (
                <PushpinOutlined
                  onClick={(e) =>
                    handlePinButtonClick(
                      assistant?.assistant_id,
                      assistant?._id,
                      assistant?.is_pinned,
                      e
                    )
                  }
                  style={{
                    marginLeft: "5px",
                    color: "gray",
                    cursor: "pointer",
                  }}
                  disabled={loading || isLoading}
                />
              )
            )}
          </div>
        </Link>
      ))}
    </>
  );

  // When in infinite scroll mode, wrap listContent in a Scrollbars container with fixed height.
  // Otherwise, render listContent in a normal div with auto height.
  const contentWrapper = seeAllMode ? (
    <Scrollbars
      autoHide
      autoHideTimeout={1000}
      autoHideDuration={200}
      style={{ width: "245px", height: "300px", border: "none" }}
      onScrollFrame={handleScrollFrame}
      renderThumbVertical={({ style, ...props }) => (
        <div
          style={{
            ...style,
            cursor: "pointer",
            backgroundColor: "rgba(255,255,255,.25)",
          }}
          {...props}
        ></div>
      )}
    >
      {listContent}
      {seeAllMode && loading && (
        <div style={{ textAlign: "center", padding: "10px" }}>
          <Spin size="small" />
          <div>Loading more agents...</div>
        </div>
      )}
    </Scrollbars>
  ) : (
    <div style={{ width: "245px" }}>{listContent}</div>
  );

  // Render the initial buttons (only on page 1) if there's no search query.
  const renderInitialButton = () => {
    if (searchQuery && searchQuery?.trim() !== "") return null;
    if (page === 1 && !showPageOneExtra) {
      return (
        <Button
          className="mb-1 w-100 custom-show-btn"
          onClick={() => {
            // Reveal the remaining 2 agents from page 1 (without an API call)
            setShowPageOneExtra(true);
          }}
        >
          <IoIosMore /> See More
        </Button>
      );
    }
    if (page === 1 && showPageOneExtra && !seeAllMode) {
      return (
        <Button
          className="mb-1 w-100 custom-show-btn"
          onClick={() => {
            // Switch to infinite scroll mode.
            setSeeAllMode(true);
            // Fetch the next package (10 agents) if available.
            if (totalPage > 1) {
              actions.setPage(2);
            }
          }}
        >
          See All Frequently Used Agents
        </Button>
      );
    }
    return null;
  };

  return (
    <div class="frequentlyUsedAgents">
      {loading && page === 1 ? (
        <p>Loading...</p>
      ) : (
        <>
          {contentWrapper}
          {renderInitialButton()}
        </>
      )}
    </div>
  );
};

export default AssistantList;
